import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { LOGINT_REDIRECT_URI_NEW, FE_ROUTES } from 'Chores/Api';

// How often (seconds) before expiry we attempt refresh
const REFRESH_SAFETY_WINDOW = 70; // Keycloak recommends refresh before <= 1 min
const REFRESH_INTERVAL_MS = 30 * 1000; // poll interval to check if token needs refresh

const KeycloakAuth = () => {
  const { keycloak } = useKeycloak();
  const refreshTimer = useRef();
  const navigate = useNavigate();

  const buildAbsoluteRedirect = (path) => {
    if (/^https?:\/\//.test(path)) {
      return path;
    }
    return window.location.origin + (path.startsWith('/') ? path : `/${path}`);
  };

  const safeRedirectUri = buildAbsoluteRedirect(LOGINT_REDIRECT_URI_NEW || FE_ROUTES.LOGINJUMP || '/');

  // Persist tokens immediately after successful authentication
  const persistTokens = () => {
    if (!keycloak?.token) return;
    localStorage.setItem('kc_token', keycloak.token);
    if (keycloak.refreshToken) localStorage.setItem('kc_refreshToken', keycloak.refreshToken);
    if (keycloak.idToken) localStorage.setItem('kc_idToken', keycloak.idToken);
  };

  // Establish a normal online session first (scope: 'openid'). Offline access escalation removed per UI simplification.
  // Guard to ensure we only trigger one immediate login attempt
  const initialLoginAttemptedRef = useRef(false);

  useEffect(() => {
    if (!keycloak) return;

    if (!keycloak.authenticated) {
      const hasStored = !!localStorage.getItem('kc_token');
      if (!hasStored && !initialLoginAttemptedRef.current) {
        initialLoginAttemptedRef.current = true;
        keycloak.login({
          scope: 'openid', // no offline_access here
          redirectUri: safeRedirectUri
        });
      } else if (hasStored && !initialLoginAttemptedRef.current) {
        // If we have stored tokens but still not authenticated after check-sso,
        // session might be invalid or inconsistent. Refrain from clearing tokens manually
        // to avoid triggering 'storage' event listeners in other tabs (which causes logout).
        // Let the IDP handle the re-authentication.
        initialLoginAttemptedRef.current = true;
        // localStorage.removeItem('kc_token');
        // localStorage.removeItem('kc_refreshToken');
        // localStorage.removeItem('kc_idToken');
        // Trigger fresh login
        keycloak.login({
          scope: 'openid',
          redirectUri: safeRedirectUri
        });
      }
    } else {
      // Persist tokens as soon as we have them
      persistTokens();
      // Immediately leave the auth landing page when authenticated
      navigate(FE_ROUTES.HOME, { replace: true });
    }
  }, [keycloak, keycloak?.authenticated, navigate, safeRedirectUri]);

  // Adaptive token refresh: use event-based refresh where possible, fallback to interval
  useEffect(() => {
    if (!keycloak?.authenticated) return;

    // Helper to clear tokens and reload
    const clearTokensAndLogin = () => {
      localStorage.removeItem('kc_token');
      localStorage.removeItem('kc_refreshToken');
      localStorage.removeItem('kc_idToken');
      // Stop refresh interval immediately
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
        refreshTimer.current = null;
      }
      // Immediately trigger Keycloak login for instant relogin
      window.location.reload();
    };

    const handleRefreshFailure = () => {
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
        refreshTimer.current = null;
      }
      window.location.reload();
    };

    // Event-based: when token expires, attempt update
    keycloak.onTokenExpired = async () => {
      try {
        const refreshed = await keycloak.updateToken(REFRESH_SAFETY_WINDOW);
        if (refreshed) {
          persistTokens();
        } else {
          // updateToken resolved but did NOT refresh (e.g. refresh token invalid)
          console.warn('[DAS] Token expired and not refreshed – forcing relogin');
          // handleRefreshFailure();
          clearTokensAndLogin();
        }
      } catch (e) {
        console.error('[DAS] Token refresh on expiry failed', e);
        // handleRefreshFailure();
        clearTokensAndLogin();
      }
    };

    // Explicit refresh success/error hooks (covers 400 Bad Request from /token)
    keycloak.onAuthRefreshSuccess = () => {
      persistTokens();
    };
    keycloak.onAuthRefreshError = () => {
      console.warn('[DAS] onAuthRefreshError – clearing tokens & relogin');
      handleRefreshFailure();
    };

    // Listen for logout event (if supported)
    keycloak.onAuthLogout = () => {
      clearTokensAndLogin();
    };

    // For online tokens with shorter lifetimes, keep a lightweight periodic check
    if (!refreshTimer.current) {
      refreshTimer.current = setInterval(async () => {
        try {
          const expiresIn = keycloak.tokenParsed?.exp ? keycloak.tokenParsed.exp * 1000 - Date.now() : 0;
          if (expiresIn > 0 && expiresIn < REFRESH_SAFETY_WINDOW * 1000) {
            const refreshed = await keycloak.updateToken(REFRESH_SAFETY_WINDOW);
            if (refreshed) {
              persistTokens();
            } else {
              // Not refreshed (e.g., refresh token unusable) – force relogin
              // console.warn('[DAS] Interval check: token due & refresh failed – relogin');
              handleRefreshFailure();
            }
          }
        } catch (e) {
          // console.error('[DAS] Interval token refresh failed', e);
          handleRefreshFailure();
        }
      }, REFRESH_INTERVAL_MS);
    }

    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [keycloak, keycloak?.authenticated]);

  // Delayed loader: avoid flashing a loading message if auth resolves quickly (<400ms)
  const [showLoader, setShowLoader] = useState(false);
  useEffect(() => {
    if (keycloak?.authenticated) {
      setShowLoader(false);
      return;
    }
    const t = setTimeout(() => setShowLoader(true), 400);
    return () => clearTimeout(t);
  }, [keycloak, keycloak?.authenticated]);

  return !keycloak.authenticated && showLoader ? (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'system-ui, sans-serif',
      color: '#666',
      fontSize: 14
    }}>
      Signing you in…
    </div>
  ) : null;
};

export default KeycloakAuth;
